import { ReactNode } from "react";

import { css } from "@compiled/react";

import { Cross } from "@/libraries/wpl/foundations/src/icons/basic/cross";

import { wacToken } from "../../../libraries/design/foundations";

export interface SmartBannerProps {
    appIcon: ReactNode;
    title: string;
    subtitle: string;
    buttonText: string;
    buttonHref: string;
    buttonAriaLabel: string;
    onClose?: () => void;
    className?: string;
    dataEventProps?: Record<string, string>;
}

const bannerStyles = css({
    display: "flex",
    alignItems: "center",
    paddingBlock: "12px",
    paddingInlineStart: "18px",
    paddingInlineEnd: "16px",
    backgroundColor: wacToken("color.neutral.light200"),
    boxShadow: `0px 1px 0px 0px ${wacToken(
        "color.neutral.light500"
    )}, 0px 0.5px 0px 0px ${wacToken("color.neutral.light500")}`,
    position: "relative",
    width: "100%",
    boxSizing: "border-box"
});

const wrapperStyles = css({
    display: "flex",
    flex: "1 0 0",
    gap: "9px",
    alignItems: "center",
    minHeight: "1px",
    minWidth: "1px"
});

const avatarContainerStyles = css({
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexShrink: 0
});

const closeButtonStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    width: "8px",
    height: "8px",
    flexShrink: 0
});

const closeIconStyles = css({
    width: "8px",
    height: "8px",
    position: "relative",
    color: wacToken("color.primary.black")
});

const appIconContainerStyles = css({
    "width": "40px",
    "height": "40px",
    "borderRadius": "10px",
    "overflow": "hidden",
    "flexShrink": 0,
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > svg": {
        width: "40px",
        height: "40px"
    }
});

const textContainerStyles = css({
    display: "flex",
    flex: "1 0 0",
    flexDirection: "column",
    gap: "3px",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingBlockStart: "2px",
    minHeight: "1px",
    minWidth: "1px"
});

const titleStyles = css({
    fontFamily: "var(--atlassian-sans-font-family)",
    fontSize: "14px",
    lineHeight: "14px",
    fontWeight: 500,
    color: wacToken("color.primary.black"),
    margin: 0,
    letterSpacing: "-0.21px",
    whiteSpace: "nowrap"
});

const subtitleStyles = css({
    fontFamily: "var(--atlassian-sans-font-family)",
    fontSize: "11px",
    lineHeight: "12px",
    fontWeight: 500,
    color: wacToken("color.neutral.light700"),
    margin: 0,
    whiteSpace: "nowrap"
});

const buttonStyles = css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBlock: "6px",
    paddingInline: "13px",
    borderRadius: "40px",
    backgroundColor: wacToken("color.primary.blue"),
    border: "none",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "15px",
    lineHeight: "18px",
    fontWeight: 400,
    color: wacToken("color.neutral.light0"),
    textDecoration: "none",
    textAlign: "center",
    letterSpacing: "-0.3px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    cursor: "pointer"
});

export const SmartBanner = ({
    appIcon,
    title,
    subtitle,
    buttonText,
    buttonHref,
    buttonAriaLabel,
    onClose,
    className,
    dataEventProps
}: SmartBannerProps) => {
    return (
        <div css={bannerStyles} className={className} role="banner">
            <div css={wrapperStyles}>
                <div css={avatarContainerStyles}>
                    {onClose && (
                        <button
                            css={closeButtonStyles}
                            onClick={onClose}
                            aria-label="Close banner"
                            type="button"
                        >
                            <span css={closeIconStyles} aria-hidden="true">
                                <Cross size="14" />
                            </span>
                        </button>
                    )}
                    <div css={appIconContainerStyles} aria-hidden="true">
                        {appIcon}
                    </div>
                </div>
                <div css={textContainerStyles}>
                    <p css={titleStyles}>{title}</p>
                    <p css={subtitleStyles}>{subtitle}</p>
                </div>
            </div>
            <a
                href={buttonHref}
                css={buttonStyles}
                aria-label={buttonAriaLabel}
                data-event="clicked"
                data-event-component="smartBannerButton"
                {...dataEventProps}
            >
                {buttonText}
            </a>
        </div>
    );
};

export default SmartBanner;
